import React from 'react'
import { pdf } from '@react-pdf/renderer'
import CourseProgressionPdfDocument from './CourseProgressionPdfDocument'
import { topicService } from '../../_state/services/topic.service'
import { formatSecondsShort } from '../../Logic/CapeeshTimeFormatting'
import { LessonType } from '../../Logic/ScenarioConstants'

function buildTopicProgressionMap(courseGroupProgression) {
    const map = {}
    if (!courseGroupProgression?.courseGroupSubPartProgressions) return map
    for (const subPart of courseGroupProgression.courseGroupSubPartProgressions) {
        if (!subPart.courseProgressions) continue
        for (const cp of subPart.courseProgressions) {
            map[cp.topicId] = cp
        }
    }
    return map
}

function buildLessonPerformanceMap(lessonProgressions) {
    const map = {}
    if (!lessonProgressions) return map
    for (const lp of lessonProgressions) {
        map[lp.customLessonId] = lp
    }
    return map
}

async function fetchTopicLessonOrder(topicId) {
    const response = await topicService.getLessonOrderForDifficulty({
        topicId,
        topicLessonDifficultyId: 0,
    })
    return response.data
}

const dateLocaleMap = { en: 'en-GB', nb: 'nb-NO', de: 'de-DE' }
const filenamePrefixMap = { en: 'Progression', nb: 'Fremdrift', de: 'Fortschritt' }

export default async function generateCourseProgressionPdf({
    courseGroup,
    courseGroupProgression,
    lessonProgressions,
    topicLessonOrderIndex,
    studentName,
    locale = 'en',
    hideScore = false,
    columnOptions,
}) {
    const topicProgressionMap = buildTopicProgressionMap(courseGroupProgression)
    const lessonPerfMap = buildLessonPerformanceMap(lessonProgressions)

    const allSubPartTopics = (courseGroup.courseGroupSubPartDtos || []).flatMap(
        (sp) => sp.courseGroupSubPartToTopicMapperDtos || []
    )

    const topicsWithProgression = allSubPartTopics.filter((t) => {
        const prog = topicProgressionMap[t.topicId]
        return prog && prog.courseNormalizedProgression > 0
    })

    const fetchPromises = topicsWithProgression.map(async (t) => {
        if (topicLessonOrderIndex && topicLessonOrderIndex[t.topicId]) {
            return { topicId: t.topicId, lessonOrder: topicLessonOrderIndex[t.topicId] }
        }
        try {
            const data = await fetchTopicLessonOrder(t.topicId)
            return { topicId: t.topicId, lessonOrder: data }
        } catch {
            return { topicId: t.topicId, lessonOrder: [] }
        }
    })

    const lessonOrderResults = await Promise.all(fetchPromises)
    const lessonOrderMap = {}
    for (const r of lessonOrderResults) {
        lessonOrderMap[r.topicId] = r.lessonOrder
    }

    const topics = topicsWithProgression.map((t) => {
        const progression = topicProgressionMap[t.topicId]
        const lessonOrder = lessonOrderMap[t.topicId] || []
        const publishedLessons = lessonOrder.filter((l) => l.published)

        const lessons = publishedLessons.map((lesson) => {
            const perf = lesson.customLessonId ? lessonPerfMap[lesson.customLessonId] : null
            const hasData = perf?.containsPerformanceData === true
            let completedDate = ''
            if (hasData && perf.completedAt) {
                completedDate = new Date(perf.completedAt).toLocaleDateString(
                    dateLocaleMap[locale] || 'en-GB',
                    { year: 'numeric', month: 'long', day: 'numeric' }
                )
            }
            return {
                name: lesson.name || 'Unnamed lesson',
                isExam: lesson.lessonType === LessonType.Exam,
                hasData,
                passed: hasData ? perf.passed : false,
                normalizedHighScore: hasData ? perf.normalizedHighScore : 0,
                stars: hasData ? perf.stars : 0,
                timeString: hasData ? formatSecondsShort(perf.timeToCompleteSeconds) : '',
                completedDate,
            }
        })

        return {
            topicName: t.topicName || 'Unnamed topic',
            topicDescription: t.topicDescription || '',
            progression: progression.courseNormalizedProgression,
            lessons,
        }
    })

    const overallProgression = courseGroupProgression?.courseGroupNormalizedProgression || 0
    const generatedDate = new Date().toLocaleDateString(dateLocaleMap[locale] || 'en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })

    const blob = await pdf(
        <CourseProgressionPdfDocument
            courseGroupName={courseGroup.name || 'Course'}
            courseGroupDescription={courseGroup.description || ''}
            courseGroupProgression={overallProgression}
            studentName={studentName || 'Student'}
            generatedDate={generatedDate}
            topics={topics}
            locale={locale}
            hideScore={hideScore}
            columnOptions={columnOptions}
        />
    ).toBlob()

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const filenamePrefix = filenamePrefixMap[locale] || 'Progression'
    link.download = `${filenamePrefix} ${studentName || 'Student'} - ${courseGroup.name || 'Course'} ${generatedDate}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}
